import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Committee, CommitteeMember } from "@/types/governance";

interface CommitteeDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  committee?: Committee;
  members: CommitteeMember[];
  owner?: CommitteeMember;
  chairperson?: CommitteeMember;
}

function inferDepartment(designation: string) {
  const value = designation.toLowerCase();
  if (value.includes("risk") || value.includes("compliance")) return "Risk & Compliance";
  if (value.includes("finance") || value.includes("cfo") || value.includes("audit")) return "Finance & Audit";
  if (value.includes("technology") || value.includes("tech") || value.includes("cyber")) return "Technology";
  if (value.includes("governance") || value.includes("board")) return "Governance";
  return "General Management";
}

function getInitials(name: string) {
  return name
    .split(" ")
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function CommitteeDetailsModal({
  open,
  onOpenChange,
  committee,
  members,
  owner,
  chairperson,
}: CommitteeDetailsModalProps) {
  const committeeMembers = committee
    ? members.filter((member) => committee.memberIds.includes(member.id))
    : [];
  const onboardedCount = committeeMembers.length;
  const minimumRequired = committee?.minimumMembersRequired ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{committee ? committee.name : "Committee details"}</DialogTitle>
          <DialogDescription>
            {committee
              ? `Level L${committee.level} • ${committee.type.replace("_", " ")} committee`
              : "Select a committee from the hierarchy graph to view details."}
          </DialogDescription>
        </DialogHeader>

        {committee ? (
          <div className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border p-3">
                <p className="text-xs uppercase text-muted-foreground">Meeting Frequency</p>
                <p className="mt-1 font-medium capitalize">{committee.meetingFrequency.replace("_", " ")}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs uppercase text-muted-foreground">Members Onboarded</p>
                <p className="mt-1 font-medium">
                  {onboardedCount}/{minimumRequired}
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs uppercase text-muted-foreground">Reporting To</p>
                <p className="mt-1 font-medium">{committee.reportingTo || "Not specified"}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs uppercase text-muted-foreground">Approval Authority</p>
                <p className="mt-1 font-medium">{committee.approvalAuthority || "Not specified"}</p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border p-4">
                <p className="text-sm font-medium">Committee Owner</p>
                {owner ? (
                  <div className="mt-3 flex items-start gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback>{getInitials(owner.name)}</AvatarFallback>
                    </Avatar>
                    <div className="space-y-1">
                      <p className="font-medium">{owner.name}</p>
                      <p className="text-sm text-muted-foreground">{owner.designation}</p>
                      <p className="text-xs text-muted-foreground">
                        Department: {inferDepartment(owner.designation)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-muted-foreground">No owner assigned.</p>
                )}
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm font-medium">Committee Chair</p>
                {chairperson ? (
                  <div className="mt-3 flex items-start gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback>{getInitials(chairperson.name)}</AvatarFallback>
                    </Avatar>
                    <div className="space-y-1">
                      <p className="font-medium">{chairperson.name}</p>
                      <p className="text-sm text-muted-foreground">{chairperson.designation}</p>
                      <p className="text-xs text-muted-foreground">
                        Department: {inferDepartment(chairperson.designation)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-muted-foreground">No chair assigned.</p>
                )}
              </div>
            </div>

            <div className="rounded-lg border p-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-medium">Member Roster</p>
                <Badge variant="outline">{committeeMembers.length} members</Badge>
              </div>
              {committeeMembers.length === 0 ? (
                <p className="text-sm text-muted-foreground">No members onboarded yet.</p>
              ) : (
                <div className="space-y-2">
                  {committeeMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex flex-col gap-2 rounded-md border p-3 md:flex-row md:items-center md:justify-between"
                    >
                      <div>
                        <p className="font-medium">{member.name}</p>
                        <p className="text-sm text-muted-foreground">{member.designation}</p>
                        <p className="text-xs text-muted-foreground">
                          Department: {inferDepartment(member.designation)}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {member.roles.map((role) => (
                          <Badge key={`${member.id}-${role}`} variant="secondary">
                            {role.replace("_", " ")}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
